/**
 * Pure helpers for `BoundArrayTable`, split out to keep the component file
 * focused on rendering (§9.4). Nothing here touches React state.
 */
import { alpha } from '@mui/material/styles';

export const ROW_PATH_RE = /^\[(\d+)\]\.(.+)$/;

export const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
export const isScalar = (v) => v === null || v === undefined || ['string', 'number', 'boolean'].includes(typeof v);

export const formatCell = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  return typeof v === 'object' ? JSON.stringify(v) : String(v);
};

// Same coercion rules as StructuredDataTree: keep numbers/booleans typed.
export const coerceCell = (raw, original) => {
  if (typeof original === 'boolean') {
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    return raw;
  }
  if (typeof original === 'number') {
    if (raw === '') return '';
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? raw : parsed;
  }
  return raw;
};

// `pendingFocus` is a server/concrete path (may contain arbitrary characters,
// e.g. a value with a literal `"`) interpolated into a `querySelector` attribute
// selector: escape it so an unescaped quote/backslash cannot break the selector.
export const escapeAttributeSelectorValue = (value) => {
  if (typeof CSS !== 'undefined' && CSS.escape) return CSS.escape(value);
  return String(value).replace(/["\\]/g, '\\$&');
};

export const sameKeys = (a, b) => a.length === b.length && a.every((k, i) => k === b[i]);
export const rowLabel = (key) => (typeof key === 'number' ? String(key) : 'nuova');

export const PAGINATION_LABELS = {
  first: 'Prima pagina',
  previous: 'Pagina precedente',
  next: 'Pagina successiva',
  last: 'Ultima pagina',
};

export const cellSx = (state) => (theme) => {
  if (state === 'invalid') {
    return {
      borderLeft: `3px solid ${theme.palette.error.main}`,
      backgroundColor: alpha(theme.palette.error.main, 0.08),
    };
  }
  if (state === 'modified') return { borderLeft: `3px solid ${theme.palette.warning.main}` };
  return {};
};

// Map<originalRowIndex, Map<leaf, instance>> — the frozen server outcome for this table.
export const collectMarkers = (root, columns, instanceIndex) => {
  const leaves = new Set(columns.map((c) => c.leaf));
  const markers = new Map();
  instanceIndex.forEach((instance, path) => {
    if (!path.startsWith(`${root}[`)) return;
    const match = ROW_PATH_RE.exec(path.slice(root.length));
    if (!match || !leaves.has(match[2])) return;
    const original = Number(match[1]);
    if (!markers.has(original)) markers.set(original, new Map());
    markers.get(original).set(match[2], instance);
  });
  return markers;
};
